import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export interface ModalLayoutProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  /** Footer primary button label */
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  /** Footer cancel button; default "İptal" */
  cancelLabel?: string;
  /** Prevent close when e.g. loading */
  closeDisabled?: boolean;
  /** Max width: "sm" | "md" | "lg". Default md */
  maxWidth?: 'sm' | 'md' | 'lg';
}

/**
 * Uygulama genelinde tutarlı modal: overlay, başlık (ikon + başlık + açıklama), içerik (p-4), footer (İptal + ana buton).
 * App Screens Upload ve Metadata Export/Translation modalları ile aynı düzen.
 */
const ModalLayout: React.FC<ModalLayoutProps> = ({
  open,
  onClose,
  title,
  icon,
  description,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  cancelLabel,
  closeDisabled = false,
  maxWidth = 'md',
}) => {
  const { t } = useTranslation();
  if (!open) return null;

  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const maxWidthClass = maxWidth === 'sm' ? 'max-w-sm' : maxWidth === 'lg' ? 'max-w-lg' : 'max-w-md';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto"
      onClick={() => !closeDisabled && onClose()}
    >
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full ${maxWidthClass} flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {icon}
            {title}
          </h3>
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {description}
            </p>
          )}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {children}
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            className="px-4 py-2 rounded-lg font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            {resolvedCancelLabel}
          </button>
          {primaryLabel && (
            <button
              type="button"
              onClick={onPrimary}
              disabled={primaryDisabled || primaryLoading}
              className="px-4 py-2 rounded-lg font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:pointer-events-none"
            >
              {primaryLoading ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {primaryLabel}
                </span>
              ) : (
                primaryLabel
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ModalLayout;
